import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTenantConfig } from "@/contexts/TenantConfigContext";
import {
  BEAUTY_SNS_LAB_BRAND,
  isBeautySnsLabConfig,
  isBeautySnsLabHost,
} from "@/config/beautySnsLab";
import { useToast } from "@/hooks/use-toast";
import { Loader2, KeyRound, Wrench } from "lucide-react";
import { parseBooleanFlag } from "@/lib/env";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return fallback;
};

const BEAUTY_STUDENT_ENTRY_PATH = "/student/dashboard";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { config: tenantConfig, loading: tenantConfigLoading } = useTenantConfig();
  const [isLoading, setIsLoading] = useState(false);
  const fallbackTimerRef = useRef<number | null>(null);
  // メンテナンスモードは /api/v1/public/tenant-config（未認証で参照可能）経由で取得する。
  // schools テーブルを直接クライアントから読むと RLS で弾かれるため使わない。
  const maintenanceMode = tenantConfig?.maintenance_mode === true;

  const isBeautyTenant =
    isBeautySnsLabConfig(tenantConfig) || isBeautySnsLabHost(window.location.hostname);
  // Beauty テナントは常にハードコード値を使用（Supabase の値を上書き）
  const brandName = isBeautyTenant
    ? BEAUTY_SNS_LAB_BRAND.name
    : (tenantConfig?.brand?.name || "学習管理システム");
  const brandTagline = isBeautyTenant
    ? BEAUTY_SNS_LAB_BRAND.tagline
    : (tenantConfig?.brand?.tagline || undefined);
  const brandLogoUrl = isBeautyTenant
    ? BEAUTY_SNS_LAB_BRAND.logo_url
    : (tenantConfig?.brand?.logo_url || undefined);
  const envPublicSignupEnabled = parseBooleanFlag(import.meta.env.VITE_PUBLIC_SIGNUP_ENABLED);
  const publicSignupEnabled =
    isBeautyTenant
      ? false
      : tenantConfig?.feature_flags?.public_signup_enabled ??
        envPublicSignupEnabled ??
        !tenantConfigLoading;
  // Beauty テナントは「管理者から...」のみ表示（ブランド名を含めない）
  const authDescription = publicSignupEnabled
    ? "アカウントにログインまたは新規登録"
    : isBeautyTenant
      ? "管理者から案内されたアカウントでログインしてください"
      : `${brandName}の管理者から案内されたアカウントでログインしてください`;

  // パスワードリカバリーモード
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState({
    password: "",
    confirmPassword: "",
  });

  // パスワードを忘れた方モード
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    fullName: "",
    confirmPassword: "",
  });

  // PASSWORD_RECOVERYイベントを検知
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, _session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
      }
    });
    return () => {
      subscription.unsubscribe();
      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
      }
    };
  }, []);

  const getDefaultRedirect = () => isBeautyTenant ? BEAUTY_STUDENT_ENTRY_PATH : "/dashboard";

  const normalizeRedirect = (path: string) => {
    if (!isBeautyTenant) return path;

    if (
      path === "/courses" ||
      path === "/student" ||
      path === "/student/dashboard"
    ) {
      return BEAUTY_STUDENT_ENTRY_PATH;
    }

    return path;
  };

  const scheduleRedirectFallback = (expectedPath: string) => {
    if (fallbackTimerRef.current) {
      window.clearTimeout(fallbackTimerRef.current);
    }

    fallbackTimerRef.current = window.setTimeout(() => {
      if (!window.location.pathname.startsWith(expectedPath)) {
        toast({
          title: "ログイン後ページへの遷移を再試行しています",
          description: "自動遷移に失敗したため再遷移します。表示されない場合は再読み込みしてください。",
        });
        navigate(expectedPath, { replace: true });
      }
    }, 4000);
  };

  const getSafeRedirect = () => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")) {
      return normalizeRedirect(redirectParam);
    }
    return null;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (error) throw error;

      if (data.user) {
        const redirectTarget = getSafeRedirect() || getDefaultRedirect();
        toast({
          title: "ログイン成功",
          description: "リダイレクトしています。表示されない場合は再読み込みしてください。",
        });
        navigate(redirectTarget);
        scheduleRedirectFallback(redirectTarget);
      }
    } catch (error: unknown) {
      toast({
        title: "ログインエラー",
        description: getErrorMessage(error, "ログインに失敗しました"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!publicSignupEnabled) {
      toast({
        title: "新規登録は停止中です",
        description: "管理者から招待または発行されたアカウントでログインしてください。",
        variant: "destructive",
      });
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      toast({
        title: "エラー",
        description: "パスワードが一致しません",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          data: {
            full_name: signupData.fullName,
          },
          emailRedirectTo: `${window.location.origin}${getDefaultRedirect()}`,
        },
      });

      if (error) throw error;

      if (data.user) {
        const redirectTarget = getSafeRedirect() || getDefaultRedirect();
        toast({
          title: "登録成功",
          description: "アカウントが作成されました。リダイレクトしています。",
        });
        navigate(redirectTarget);
        scheduleRedirectFallback(redirectTarget);
      }
    } catch (error: unknown) {
      toast({
        title: "登録エラー",
        description: getErrorMessage(error, "登録に失敗しました"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // パスワードリセットメール送信処理
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: "メール送信完了",
        description: "パスワード設定用のメールを送信しました。メールをご確認ください。",
      });
      setIsForgotPassword(false);
      setForgotEmail("");
    } catch (error: unknown) {
      toast({
        title: "送信エラー",
        description: getErrorMessage(error, "メールの送信に失敗しました"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // パスワード更新処理
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (recoveryPassword.password.length < 8) {
      toast({
        title: "エラー",
        description: "パスワードは8文字以上で設定してください",
        variant: "destructive",
      });
      return;
    }

    if (recoveryPassword.password !== recoveryPassword.confirmPassword) {
      toast({
        title: "エラー",
        description: "パスワードが一致しません",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: recoveryPassword.password,
      });

      if (error) throw error;

      toast({
        title: "パスワード設定完了",
        description: "新しいパスワードが設定されました。ログイン後ページにリダイレクトしています。",
      });
      setIsRecoveryMode(false);
      const redirectTarget = getSafeRedirect() || getDefaultRedirect();
      navigate(redirectTarget);
      scheduleRedirectFallback(redirectTarget);
    } catch (error: unknown) {
      toast({
        title: "パスワード設定エラー",
        description: getErrorMessage(error, "パスワードの設定に失敗しました"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // パスワードを忘れた方モードの場合
  if (isForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <KeyRound className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">パスワードをリセット</CardTitle>
            <CardDescription className="text-center">
              登録済みのメールアドレスを入力してください。<br />
              パスワード設定用のリンクをお送りします。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">メールアドレス</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="email@example.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                メールを送信
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setForgotEmail("");
                  }}
                  className="text-sm text-muted-foreground hover:text-primary underline"
                >
                  ログイン画面に戻る
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // パスワードリカバリーモードの場合
  if (isRecoveryMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <KeyRound className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">パスワード設定</CardTitle>
            <CardDescription className="text-center">
              新しいパスワードを設定してください
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">新しいパスワード</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="8文字以上"
                  value={recoveryPassword.password}
                  onChange={(e) => setRecoveryPassword({ ...recoveryPassword, password: e.target.value })}
                  required
                  disabled={isLoading}
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">パスワード（確認）</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  placeholder="もう一度入力"
                  value={recoveryPassword.confirmPassword}
                  onChange={(e) => setRecoveryPassword({ ...recoveryPassword, confirmPassword: e.target.value })}
                  required
                  disabled={isLoading}
                  minLength={8}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                パスワードを設定
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // メンテナンスモード: ログイン不可
  if (maintenanceMode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {brandLogoUrl && (
              <div className="mb-4 flex justify-center">
                <img
                  src={brandLogoUrl}
                  alt={`${brandName} ロゴ`}
                  className="max-h-24 max-w-[280px] object-contain"
                />
              </div>
            )}
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-amber-100 p-4">
                <Wrench className="h-8 w-8 text-amber-600" />
              </div>
            </div>
            <CardTitle className="text-xl">メンテナンス中</CardTitle>
            <CardDescription className="mt-2">
              現在、システムのメンテナンスを行っております。
              <br />
              ご不便をおかけしますが、しばらくお待ちください。
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          {brandLogoUrl && (
            <div className="mb-4 flex justify-center">
              <img
                src={brandLogoUrl}
                alt={`${brandName} ロゴ`}
                className="max-h-24 max-w-[280px] object-contain"
              />
            </div>
          )}
          {!brandLogoUrl && (
            <CardTitle className="text-2xl text-center">{brandName}</CardTitle>
          )}
          {brandTagline && (
            <p className="text-center text-sm text-muted-foreground">{brandTagline}</p>
          )}
          <CardDescription className="text-center">
            {authDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {publicSignupEnabled ? (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">ログイン</TabsTrigger>
                <TabsTrigger value="signup">新規登録</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">メールアドレス</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="email@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">パスワード</Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    ログイン
                  </Button>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(true)}
                      className="text-sm text-muted-foreground hover:text-primary underline"
                    >
                      パスワードを忘れた方
                    </button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">氏名</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="山田太郎"
                      value={signupData.fullName}
                      onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">メールアドレス</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="email@example.com"
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">パスワード</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">パスワード（確認）</Label>
                    <Input
                      id="signup-confirm"
                      type="password"
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    新規登録
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">メールアドレス</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="email@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">パスワード</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                ログイン
              </Button>
              <p className="text-sm text-muted-foreground">
                初回ログイン情報が分からない場合は、管理者にアカウント発行を依頼してください。
              </p>
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-sm text-muted-foreground hover:text-primary underline"
                >
                  パスワードを忘れた方
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Footer Links */}
      <div className="mt-6 text-center text-sm text-muted-foreground space-x-4">
        <Link to="/terms" className="hover:text-primary underline">
          利用規約
        </Link>
        <Link to="/privacy" className="hover:text-primary underline">
          プライバシーポリシー
        </Link>
      </div>
    </div>
  );
};

export default Auth;
